import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { StatisticalAnalysisResponse } from "@/services/statistical-analysis-service";
import {
  EVENTS,
  EVENTS_PROPERTIES,
} from "@/features/analytics/constants/events";
interface OutlierConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  statisticalData?: StatisticalAnalysisResponse;
  onAnalyticsEvent?: (
    eventName: keyof typeof EVENTS,
    properties: EVENTS_PROPERTIES[keyof typeof EVENTS],
  ) => void;
}

export const OutlierConfirmationDialog: React.FC<
  OutlierConfirmationDialogProps
> = ({ isOpen, onClose, onConfirm, statisticalData, onAnalyticsEvent }) => {
  const derived = React.useMemo(() => {
    if (!statisticalData) return null;

    const priceAnalysis = statisticalData.analyses.monthlyPrice;
    const isExtremePrice =
      priceAnalysis.outlierDetection.status !== "within_range";
    const priceOutlierType =
      priceAnalysis.outlierDetection.status === "below_outlier"
        ? "low"
        : "high";
    const isExtremeRatio =
      statisticalData.spaceRatioValidation?.status === "below_minimum";

    let primaryReason: "price_outlier" | "size-capacity_outlier" =
      "price_outlier";
    if (isExtremeRatio && !isExtremePrice) {
      primaryReason = "size-capacity_outlier";
    }

    const outlierType: "low" | "high" = isExtremePrice
      ? priceOutlierType
      : "low";

    return { primaryReason, outlierType };
  }, [statisticalData]);

  const handleDialogView = React.useCallback(() => {
    if (!derived) return;
    onAnalyticsEvent?.("OUTLIER_DIALOG_VIEWED", {
      reason: derived.primaryReason,
      outlier_type: derived.outlierType,
    });
  }, [onAnalyticsEvent, derived]);

  const handleConfirm = () => {
    if (derived) {
      onAnalyticsEvent?.("OUTLIER_DIALOG_CONFIRMED", {
        reason: derived.primaryReason,
        outlier_type: derived.outlierType,
      });
    }
    onConfirm();
  };

  const handleCancel = () => {
    if (derived) {
      onAnalyticsEvent?.("OUTLIER_DIALOG_CANCELLED", {
        reason: derived.primaryReason,
        outlier_type: derived.outlierType,
      });
    }
    onClose();
  };

  React.useEffect(() => {
    if (isOpen && statisticalData) {
      handleDialogView();
    }
  }, [isOpen, statisticalData, handleDialogView]);

  if (!statisticalData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[512px]">
        <DialogHeader>
          <DialogTitle className="text-lg">
            Tem certeza que quer publicar assim?
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-[#71717A]">
            Você está tentando publicar esta sala com informações que podem
            reduzir o conforto ou o interesse dos clientes. Deseja continuar
            mesmo assim?
          </p>

          <div className="flex gap-3 pt-2 justify-end">
            <Button variant="outline" onClick={handleConfirm} className="">
              Continuar mesmo assim
            </Button>
            <Button onClick={handleCancel} className="">
              Voltar e corrigir
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
